import { ReactNode } from 'react';
import { Breadcrumb, Button } from '@carbon/react';
import { ArrowLeft } from '@carbon/react/icons';
import { Crumb } from 'components/page-header';

import styles from './workspace-shell-header.module.scss';

export interface WorkspaceShellHeaderProps {
  /** Optional back affordance — renders an ArrowLeft icon-only button on the
   *  leading edge. */
  onBack?: () => void;
  /** Accessible description for the back button. */
  backLabel?: string;
  /** Primary title — typically the workspace name. */
  title: ReactNode;
  /** Optional inline subtitle (entity name, type curve label, etc.). */
  subtitle?: ReactNode;
  /** Right-side actions. The shell's SCSS recolors common Carbon button
   *  kinds (ghost / tertiary / primary) for the dark background, so callers
   *  pass plain Carbon `<Button>`s without per-button overrides. */
  actions?: ReactNode;
  /** `[path, label]` pairs above the title row, as `PageHeader` takes them; the last is the current page. */
  breadcrumbs?: [string, ReactNode][];
}

/**
 * Dark-bar workspace header shared across workspace shells. Renders the
 * back button → title + subtitle → right-side actions row on a Gray-90
 * background. Currently consumed by Interest Certification; Type Curve has
 * its own bespoke `TypeCurveWorkspaceHeader` that could migrate to this
 * shared shell in a follow-up.
 *
 * @param {WorkspaceShellHeaderProps} props - Component props.
 * @return {JSX.Element} The rendered dark workspace header bar.
 */
export const WorkspaceShellHeader = ({
  onBack,
  backLabel = 'Leave workspace',
  title,
  subtitle,
  actions,
  breadcrumbs,
}: WorkspaceShellHeaderProps): JSX.Element => (
  <header className={styles.shell}>
    {breadcrumbs && breadcrumbs.length > 0 && (
      <Breadcrumb noTrailingSlash className={styles.breadcrumbs}>
        {breadcrumbs.map(([location, text], index) => (
          <Crumb
            key={location}
            location={location}
            text={text}
            isCurrent={index === breadcrumbs.length - 1}
          />
        ))}
      </Breadcrumb>
    )}
    <div className={styles.row}>
      <div className={styles.leading}>
        {onBack && (
          <Button
            kind="ghost"
            size="md"
            hasIconOnly
            iconDescription={backLabel}
            className={styles.iconOnDark}
            onClick={onBack}
            renderIcon={(props) => <ArrowLeft size={20} {...props} />}
          />
        )}
        <div className={styles.titles}>
          <span className={styles.workspaceName}>{title}</span>
          {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        </div>
      </div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </div>
  </header>
);
